import { laboratoryConfig } from './laboratoryConfig';

const fallbackOrder = 999;
const fallbackCategory = 'otros';

const getExamCategory = (exam) => exam?.inputs?.exam_group?.category || fallbackCategory;

export const isGroupComplete = (examGroup) => {
  if (!examGroup?.exam_group_id) return false;
  const totalExams = examGroup.exam_group_total_exams || 0;
  const matchedExams = examGroup.matched_exams_count || 0;
  return totalExams > 0 && matchedExams >= totalExams;
};

export const getGroupId = (exam) => {
  const examGroup = exam.inputs?.exam_group;
  return examGroup?.exam_group_id ? `group-${examGroup.exam_group_id}` : `exam-${exam.id}`;
};

export const getGroupedAndSortedLaboratoryExams = (allExams) => {
  const groups = {};
  allExams.forEach(exam => {
    const category = getExamCategory(exam);
    if (!groups[category]) {
      const configOrder = laboratoryConfig[category.toLowerCase()]?.order ?? fallbackOrder;
      groups[category] = { category, order: configOrder, exams: [] };
    }
    groups[category].exams.push(exam);
  });

  const originalPositions = new Map(allExams.map((exam, index) => [exam.id, index]));
  const groupMinIndices = {};
  allExams.forEach(exam => {
    const groupId = getGroupId(exam);
    const examIndex = originalPositions.get(exam.id);
    if (groupMinIndices[groupId] === undefined || examIndex < groupMinIndices[groupId]) {
      groupMinIndices[groupId] = examIndex;
    }
  });

  const sortedGroups = Object.values(groups).sort((a, b) => a.order - b.order);
  sortedGroups.forEach(group => {
    const examsWithSortKeys = group.exams.map(exam => {
      const examGroup = exam.inputs?.exam_group;
      return {
        exam,
        isComplete: isGroupComplete(examGroup),
        groupOrder: examGroup?.exam_group_order ?? fallbackOrder,
        groupMinIndex: groupMinIndices[getGroupId(exam)],
        examOrderInGroup: examGroup?.exam_group_exams_order ?? fallbackOrder,
      };
    });

    examsWithSortKeys.sort((firstExamInfo, secondExamInfo) => {
      if (firstExamInfo.isComplete !== secondExamInfo.isComplete) {
        return firstExamInfo.isComplete ? -1 : 1;
      }
      if (firstExamInfo.groupOrder !== secondExamInfo.groupOrder) {
        return firstExamInfo.groupOrder - secondExamInfo.groupOrder;
      }
      if (firstExamInfo.groupMinIndex !== secondExamInfo.groupMinIndex) {
        return firstExamInfo.groupMinIndex - secondExamInfo.groupMinIndex;
      }
      return firstExamInfo.examOrderInGroup - secondExamInfo.examOrderInGroup;
    });

    group.exams = examsWithSortKeys.map(item => item.exam);
  });

  return sortedGroups;
};

export const getLaboratoryCategoryInfo = ({
  exam,
  index,
  sortedExams,
  seenCategories,
  seenExamGroupIds,
  collapsedCategories,
  toggleCategoryCollapse,
}) => {
  const examGroup = exam.inputs?.exam_group;
  const category = getExamCategory(exam);
  const isFirstInCategory = !seenCategories.has(category);
  seenCategories.add(category);

  const examGroupId = examGroup?.exam_group_id;
  const examGroupName = examGroup?.exam_group_name;
  const isCompleteGroup = isGroupComplete(examGroup);
  const isFirstInGroup = examGroupId && !seenExamGroupIds.has(examGroupId);
  if (examGroupId) seenExamGroupIds.add(examGroupId);

  const previousExam = index > 0 ? sortedExams[index - 1] : null;
  const previousExamGroup = previousExam?.inputs?.exam_group;
  const isPreviousInSameCategory = previousExam && getExamCategory(previousExam) === category;

  const showGroupHeader = isCompleteGroup && examGroupName && isFirstInGroup;
  
  const wasPreviousANamedCompleteGroup = isPreviousInSameCategory && 
    isGroupComplete(previousExamGroup) && 
    previousExamGroup?.exam_group_name;
  const belongsToSameGroupAsPrevious = examGroupId && examGroupId === previousExamGroup?.exam_group_id;
  
  const showGroupSeparator = !showGroupHeader && wasPreviousANamedCompleteGroup && !belongsToSameGroupAsPrevious;

  const previousEndsInFieldGroup = previousExam?.inputs?.procedures?.at(-1)?.field_group_id;
  const startsOutsideFieldGroup = !exam.inputs?.procedures?.[0]?.field_group_id;
  const showFieldGroupSeparator = isPreviousInSameCategory && !showGroupHeader && !showGroupSeparator &&
    previousEndsInFieldGroup && startsOutsideFieldGroup;

  const isLastInCategory = index === sortedExams.length - 1 ||
    getExamCategory(sortedExams[index + 1]) !== category;

  return {
    category,
    config: laboratoryConfig[category.toLowerCase()],
    isFirstInCategory,
    isCollapsed: collapsedCategories.has(category),
    onToggleCollapse: toggleCategoryCollapse,
    examGroupName: showGroupHeader ? examGroupName : null,
    showGroupSeparator,
    showFieldGroupSeparator,
    isLastInCategory,
  };
};
